import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import { Button, FormControl, FormErrorMessage, Text } from '@chakra-ui/react'
import type { FieldProps } from 'formik'
import { Field, Form, Formik } from 'formik'
import { ReactElement, useCallback, useContext } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { CachePolicies, useFetch } from 'use-http'
import { IKReactSelectCreatable } from '../../../../ui-kit'
import useToast from '../../../hooks/useToast'
import { ClientTagRequestResponse, ClientTagResponse } from '../../../interfaces'
import { useDebounce } from '../../../hooks/useDebounce'
import {
  DEFAULT_CLIENT_TAG_LIMIT,
  DEFAULT_CLIENT_TAG_SKIP,
} from '../../../constants'
import { UserContext } from '../../../providers'
import { formatTags } from '../../../../utils/mediaLibrary'

interface Props {
  fileId: string
  data: string[]
  isOpen: boolean
  onClose(): void
  getFileDetails(): void
}

interface FormProps {
  tags: Array<{
    label: string
    value: string
  }>
}

const AddTagsModal = ({
  fileId,
  data,
  isOpen,
  onClose,
  getFileDetails,
}: Props): ReactElement => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)

  const toast = useToast()
  const { request } = useFetch<ClientTagRequestResponse>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const [suggestedTags, setSuggestedTags] = useState<ClientTagResponse[]>([])
  const [search, setSearch] = useState<string>()
  const debouncedSearchTerm = useDebounce(search, 500)

  const getSuggestedTags = useCallback(
    async debouncedSearchTerm => {
      if (user?.clientNumber && isOpen) {
        let url = `/api/clients/${user?.clientNumber}/client-tags?limit=${DEFAULT_CLIENT_TAG_LIMIT}&skip=${DEFAULT_CLIENT_TAG_SKIP}`
        if (debouncedSearchTerm) url = `${url}&label=${debouncedSearchTerm}`

        const response = await request.get(url)

        if (request.error) {
          toast({
            description: t(
              'error_fetching_data',
              'Error in fetching data. Try refreshing the page.',
            ),
            status: 'error',
            isClosable: true,
            duration: 5000,
            position: 'top-right',
            variant: 'outline',
          })
        } else if (response?.tagsData && Array.isArray(response.tagsData)) {
          setSuggestedTags(response.tagsData)
        }
      }
    },
    [user, isOpen],
  )

  useEffect(() => {
    getSuggestedTags(debouncedSearchTerm)
  }, [debouncedSearchTerm, user, isOpen])

  const tagsOptions = useMemo(() => {
      return suggestedTags.map(data => ({
        label: data.label,
        value: data.name,
      }))
  }, [suggestedTags])

  const initialData = useMemo(() => {
    return data.map(item => ({ label: item, value: item.toLowerCase() }))
  }, [suggestedTags])

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <Formik<FormProps>
          initialValues={{ tags: initialData }}
          onSubmit={async values => {
            const tagsArrToUpdate = values.tags.map(tag => tag.label)
            await request.patch(`/api/v2/files/${fileId}/details`, {
              tags: tagsArrToUpdate.length > 0 ? tagsArrToUpdate : null,
            })

            if (request.error) {
              toast({
                description: t('something_went_wrong', 'Something went wrong!'),
                status: 'error',
                isClosable: true,
                duration: 5000,
                position: 'top-right',
                variant: 'outline',
              })
              return
            }

            onClose()
            void getFileDetails()

            toast({
              title: t(
                'file_tags_updated_successfully',
                'File Tags updated successfully',
              ),
              status: 'success',
              isClosable: true,
              duration: 5000,
              position: 'top-right',
              variant: 'outline',
            })
          }}
        >
          {({ isSubmitting, dirty }) => (
            <Form>
              <ModalHeader>{t('edit_tags', 'Edit tags')}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Field name="tags">
                  {({ field, form }: FieldProps<string, FormProps>) => {
                    const getValue = () => {
                      const valuesArr = Array.isArray(field.value)
                        ? field.value
                        : []
                      return valuesArr
                    }

                    const onChange = options => {
                      const updatedOptions = formatTags(options)
                      form.setFieldValue(field.name, updatedOptions)
                    }

                    return (
                      <FormControl
                        isInvalid={Boolean(
                          form.errors.tags && form.touched.tags,
                        )}
                        isRequired
                      >
                        <Text mb={4}>
                          <Trans t={t} i18nKey="edit_tags_modal_description">
                            Edit tags on file
                          </Trans>
                        </Text>
                        <IKReactSelectCreatable
                          {...field}
                          isMulti
                          defaultValue={initialData}
                          options={tagsOptions}
                          inputValue={search}
                          onInputChange={value => setSearch(value)}
                          onChange={onChange}
                          value={getValue()}
                          isClearable={false}
                          skipExactMatch={true}
                        />
                        <FormErrorMessage marginBottom="-2.0408em">
                          {form.errors.tags}
                        </FormErrorMessage>
                      </FormControl>
                    )
                  }}
                </Field>
              </ModalBody>
              <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
                <Button onClick={onClose} variant="outline">
                  {t('cancel')}
                </Button>
                <Button
                  type="submit"
                  isLoading={isSubmitting}
                  disabled={!dirty}
                >
                  {t('save', 'Save')}
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default AddTagsModal
